/**
 * Public route configuration
 *
 * Defines which routes are accessible without authentication.
 * Used by both the 401 interceptor and routing logic to prevent
 * redirect loops for unauthenticated users.
 */

/**
 * Routes that are exactly public (no authentication required)
 */
const PUBLIC_ROUTES = new Set([
  "/",
  "/login",
  "/register",
  "/verify-email",
  "/check-email",
  "/forgot-password",
  "/reset-password",
  "/terms-of-service",
  "/privacy-policy",
  "/security",
  "/dpa",
  "/classroom-oauth", // IT admin guide for Google Classroom setup
  "/ai-detection", // AI detector marketing landing page
  "/billing/success", // Users return here from Stripe checkout
  "/billing/canceled", // Users return here from Stripe checkout
  "/account-deleted", // User is logged out after account deletion
]);

/**
 * Route prefixes that indicate public routes
 * Any route starting with these prefixes is public
 */
const PUBLIC_ROUTE_PREFIXES = [
  "/portal/",
  "/terms-",
  "/privacy-",
];

/**
 * Checks if a given pathname is a public route (accessible without authentication)
 *
 * @param pathname - The URL pathname to check (e.g., window.location.pathname)
 * @returns true if the route is public, false if it requires authentication
 */
export function isPublicRoute(pathname: string): boolean {
  // Check exact matches first (most common case)
  if (PUBLIC_ROUTES.has(pathname)) {
    return true;
  }

  // Check prefix matches
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
